import React, { useMemo, useRef, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { generateUUIDV4 } from 'util/Generator';
import { ZindexValues } from 'config/ZindexValues';

interface CustomTableContentWrapperProps {
  text: string;
  clipSize?: number;
  showEllipsis?: boolean;
  asLink?: boolean;
  tooltipText?: string;
  'data-testid'?: string;
}

interface TooltipPosition {
  top: number;
  left: number;
}

const useTooltipId = (): string => {
  return useMemo(() => generateUUIDV4(), []);
};

const useClippedText = (text: string, clipSize: number, showEllipsis: boolean): string => {
  return useMemo(() => {
    try {
      if (text == undefined || text.length == 0) return 'N/A';
      if (text.length > clipSize) {
        return showEllipsis ? text.substring(0, clipSize) + '...' : text.substring(0, clipSize);
      }
      return text.substring(0, clipSize);
    } catch (e) {
      console.error(e);
      return text;
    }
  }, [text, clipSize, showEllipsis]);
};

const useTooltipPosition = (parentRef: React.RefObject<HTMLDivElement>, isHovered: boolean): TooltipPosition | null => {
  const [position, setPosition] = useState<TooltipPosition | null>(null);

  useEffect(() => {
    if (!isHovered || !parentRef.current) {
      setPosition(null);
      return;
    }

    const updatePosition = (): void => {
      if (!parentRef.current) return;

      const rect = parentRef.current.getBoundingClientRect();
      setPosition({
        top: rect.top,
        left: rect.left,
      });
    };

    updatePosition();

    const handleScroll = (): void => {
      updatePosition();
    };

    const handleResize = (): void => {
      updatePosition();
    };

    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
    };
  }, [isHovered, parentRef]);

  return position;
};

const CustomTableContentWrapper: React.FC<CustomTableContentWrapperProps> = ({
  text,
  clipSize = 40,
  showEllipsis = false,
  asLink = false,
  tooltipText,
  'data-testid': dataTestId,
}) => {
  const id = useTooltipId();
  const textString = String(text ?? '');
  const parsed = useClippedText(textString, clipSize, showEllipsis);
  const hasCustomTooltip = tooltipText != undefined && tooltipText.length > 0;
  const shouldShowTooltip = hasCustomTooltip || textString.length >= clipSize;
  const tooltipContent = hasCustomTooltip ? tooltipText : textString;
  const parentRef = useRef<HTMLDivElement>(null);
  const [isHovered, setIsHovered] = useState(false);
  const tooltipPosition = useTooltipPosition(parentRef, isHovered && shouldShowTooltip);

  return (
    <>
      <div
        ref={parentRef}
        data-testid={dataTestId}
        id={'parent' + id}
        className={`flex w-auto text-[11px] 2xl:text-md relative h-full group items-center ${
          asLink ? 'text-[#458EF7] underline cursor-pointer' : 'text-f-black-3'
        }`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <p>{parsed} </p>
      </div>
      {shouldShowTooltip &&
        tooltipPosition &&
        createPortal(
          <div
            className={'fixed before:w-0 before:h-0 before:border-t-[10px] before:border-t-transparent before:border-r-[10px] before:border-r-black before:absolute before:-left-0 before:top-5 before:rotate-[90deg] before:border-b-[5px] before:border-b-transparent px-2 py-2 rounded-md bg-black text-white'}
            style={{
              zIndex: Number(ZindexValues.TOOLTIP),
              top: `${tooltipPosition.top}px`,
              left: `${tooltipPosition.left}px`,
              transform: 'translateY(-100%)',
            }}
            data-testid={`tooltip-${id}`}
            id={id}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            {tooltipContent || ''}
          </div>,
          document.body,
        )}
    </>
  );
};

export default CustomTableContentWrapper;
